/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ProcessError } from '@/types/models';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Dialog } from 'primereact/dialog';
import { Messages } from 'primereact/messages';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  errors: Array<ProcessError>;
};

export const ProjectProcessesErrors: React.FC<Props> = ({ errors }) => {
  const { t, i18n } = useTranslation();
  const msgsRef = useRef<Messages>(null);
  const [dialogVisible, setDialogVisible] = useState(false);

  const Content = () => {
    return (
      <div className="flex align-items-center justify-content-between">
        <div className="text-md">{t('processes.errors.count', { count: errors.length })}&nbsp;</div>
        <div className="font-semibold text-color underline cursor-pointer" onClick={() => setDialogVisible(true)}>
          {t('processes.errors.see_details')}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (msgsRef.current) {
      msgsRef.current.clear();
      msgsRef.current?.show({
        severity: 'error',
        summary: t('processes.errors.summary'),
        detail: <Content />,
        sticky: true,
        closable: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  return (
    <>
      <Messages ref={msgsRef} pt={{ summary: { className: 'text-m' } }} className="text-xs" />

      <Dialog
        header={t('processes.errors.dialog_title')}
        visible={dialogVisible}
        style={{ width: '50vw' }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
        onHide={() => setDialogVisible(false)}
      >
        <Accordion multiple>
          {errors.map((error) => (
            <AccordionTab
              pt={{
                headerAction: { className: 'bg-red-50 text-sm text-red-500 ' },
                content: { className: 'bg-red-50' }
              }}
              key={error.id}
              header={error.id}
            >
              <pre className="m-0 bg-red-50 text-xs text-red-500 ">
                {error.detail || t('processes.errors.no_detail')}
              </pre>
            </AccordionTab>
          ))}
        </Accordion>
      </Dialog>
    </>
  );
};
